/**
 * drawnSurface — THE RIDER LAW's one solve (shader gaps, 2026-09-30): the height gameplay stands on is the DRAWN sea, and
 * the drawn sea over a point is the displaced triangle of the owner's mesh that covers it — the rasteriser's own surface,
 * never the continuous wave field (the mesh interpolates linearly between vertices: the chord sag of an 8 m triangle is
 * decimetres on the Gerstner swell) and never waves the mesh does not displace (those live in the per-pixel normal).
 *
 * Moved out of FftWaveProvider (R5) so the Gerstner sea's riders stand on the same triangles: each provider supplies its
 * displaced vertex, this walks the owner's grid. The triangle split is three's PlaneGeometry (after `rotateX(−π/2)`):
 * cell (i, j) has corners a = (i, j), b = (i, j + 1), c = (i + 1, j + 1), d = (i + 1, j); the lower triangle (a, b, d)
 * sits under the b–d diagonal, the upper (b, c, d) above it.
 */

/** The owner's drawn mesh: vertex (i, j) stands at ABSOLUTE (originX + i·spacing, originZ + j·spacing), i, j ∈ [0, cells]. */
export interface DrawnGrid {
  originX: number;
  originZ: number;
  spacing: number;
  cells: number;
}

/** Write grid vertex (i, j)'s DISPLACED position into `out`: [absolute x, height, absolute z]. */
export type DrawnVertexFn = (i: number, j: number, out: Float64Array) => void;

const vx = new Float64Array(3), vy = new Float64Array(3), vz = new Float64Array(3);
const scratch = new Float64Array(3);

/**
 * The height of the displaced mesh over absolute (x, z): start in the undisplaced cell, then walk across the edge opposite
 * the most negative barycentric until the point lies inside (at most 8 steps — a displacement never carries a vertex more
 * than a cell or two). Off the grid the nearest edge triangle answers.
 */
export function solveDrawnTriangle(x: number, z: number, g: DrawnGrid, vertex: DrawnVertexFn): number {
  const sp = g.spacing;
  let ci = Math.min(g.cells - 1, Math.max(0, Math.floor((x - g.originX) / sp)));
  let cj = Math.min(g.cells - 1, Math.max(0, Math.floor((z - g.originZ) / sp)));
  const fu = (x - g.originX) / sp - ci, fv = (z - g.originZ) / sp - cj;
  let upper = fu + fv > 1;
  let l0 = 1 / 3, l1 = 1 / 3, l2 = 1 / 3;
  for (let step = 0; step < 8; step++) {
    // corners: a = (i, j), b = (i, j + 1), c = (i + 1, j + 1), d = (i + 1, j)
    for (let n = 0; n < 3; n++) {
      const ii = upper ? (n === 0 ? ci : ci + 1) : (n === 2 ? ci + 1 : ci);
      const jj = upper ? (n === 2 ? cj : cj + 1) : (n === 1 ? cj + 1 : cj);
      vertex(ii, jj, scratch);
      vx[n] = scratch[0]; vy[n] = scratch[1]; vz[n] = scratch[2];
    }
    // Barycentrics of (x, z) in the displaced XZ triangle.
    const d = (vz[1] - vz[2]) * (vx[0] - vx[2]) + (vx[2] - vx[1]) * (vz[0] - vz[2]);
    if (Math.abs(d) < 1e-12) break;
    l0 = ((vz[1] - vz[2]) * (x - vx[2]) + (vx[2] - vx[1]) * (z - vz[2])) / d;
    l1 = ((vz[2] - vz[0]) * (x - vx[2]) + (vx[0] - vx[2]) * (z - vz[2])) / d;
    l2 = 1 - l0 - l1;
    const lo = Math.min(l0, l1, l2);
    if (lo >= -1e-9) break;
    // Step across the edge opposite the most negative barycentric.
    if (!upper) {
      upper = true;                                        // every lower-triangle edge borders an upper triangle
      if (lo === l2) ci -= 1; else if (lo === l1) cj -= 1; // opposite d: (i − 1, j); opposite b: (i, j − 1); opposite a: this cell
    } else {
      upper = false;
      if (lo === l0) ci += 1; else if (lo === l2) cj += 1; // opposite b: (i + 1, j); opposite d: (i, j + 1); opposite c: this cell
    }
    if (ci < 0 || cj < 0 || ci >= g.cells || cj >= g.cells) {
      ci = Math.min(g.cells - 1, Math.max(0, ci));
      cj = Math.min(g.cells - 1, Math.max(0, cj));
      break;
    }
  }
  return l0 * vy[0] + l1 * vy[1] + l2 * vy[2];
}
